import * as preact from "preact";

import * as keyboardShortcuts from "keyboardShortcuts";
import UserWordModel from "models/UserWordModel";
import models from "models/models";
import * as practiceUtils from "practiceUtils";
import { ChallengeProps } from "practiceUtils";
import ExplanationView from "views/ExplanationView";
import PracticeContextView from "views/PracticeContextView";
import PracticePlayTTSView from "views/PracticePlayTTSView";
import TransliterationView from "./TransliterationView";

interface State {
  clozeSentence: practiceUtils.ClozeSentence;
}

const cardModes = [
  {
    type: "active",
    displayString: "Active (includes typing)",
  },
  {
    type: "passive",
    displayString: "Passive (no typing)",
  },
  {
    type: "delete",
    displayString: "Delete this {{wordOrPhraseString}}",
  },
] as const;

class IntroChallengeView extends preact.Component<ChallengeProps, State> {
  userWords: Backbone.Collection<UserWordModel>;

  constructor(props: ChallengeProps) {
    super(props);

    this.state = {
      clozeSentence: practiceUtils.getClozeSentence(
        this.props.userWord,
        this.props.selectedContextIndex
      ),
    };

    setTimeout(() => {
      this.props.completedCallback({
        type: "allow-continue",
      });
    }, 1000);
  }

  render() {
    const { userWord } = this.props;
    const { clozeSentence } = this.state;

    const alternativeTranslations = userWord.get("alternativeTranslations");

    const wordOrPhraseString = userWord.isPhrase() ? "phrase" : "word";

    return (
      <div class="introChallenge">
        <h3 class="newWord">NEW {wordOrPhraseString.toUpperCase()}</h3>
        <h2>
          <PracticePlayTTSView
            text={userWord.get("word")}
            language={userWord.get("language")}
          />{" "}
          <span class="transliterationContainer">
            <TransliterationView
              text={userWord.get("word")}
              language={userWord.get("language")}
            />
            <div>{userWord.get("word")}</div>
          </span>
        </h2>

        <p>
          <PracticeContextView
            clozeSentence={clozeSentence}
            mode={{ type: "complete" }}
          />
        </p>

        {practiceUtils.showContextSource ? (
          <p>
            <small>
              From:{" "}
              {
                userWord.get("contexts")[this.props.selectedContextIndex]
                  ?.bookID?.title
              }
            </small>
          </p>
        ) : null}

        <p>
          Meaning: <strong>{userWord.get("translation")}</strong>
          {alternativeTranslations.length > 0
            ? ` / ${userWord.get("alternativeTranslations").join(" / ")}`
            : null}
        </p>

        <ExplanationView
          word={userWord.get("word")}
          context={clozeSentence.map((part) => part.text).join("")}
          language={userWord.get("language")}
          showInFirstLanguage={false}
          cacheOnly={true}
          existingTranslations={[
            userWord.get("translation"),
            ...userWord.get("alternativeTranslations"),
          ]}
          onExplanationFetched={() => this.props.showingExplanation()}
        />
        <ExplanationView
          word={userWord.get("word")}
          context={clozeSentence.map((part) => part.text).join("")}
          language={userWord.get("language")}
          showInFirstLanguage={true}
          cacheOnly={true}
          existingTranslations={[
            userWord.get("translation"),
            ...userWord.get("alternativeTranslations"),
          ]}
          onExplanationFetched={() => this.props.showingExplanation()}
        />

        {models.user.get("practiceType") === "new" ? (
          <div class="newWordOptions">
            <p>
              <strong>Learning mode for this word</strong>
            </p>
            <ul>
              {cardModes.map((mode, modeIndex) => (
                <li>
                  <label>
                    <input
                      type="radio"
                      name="learn-mode"
                      checked={this.props.introCardMode === mode.type}
                      onClick={() => {
                        this.props.setCardMode(mode.type);
                      }}
                    ></input>{" "}
                    <small>[{modeIndex + 1}]</small>{" "}
                    {mode.displayString.replace(
                      "{{wordOrPhraseString}}",
                      wordOrPhraseString
                    )}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    );
  }

  componentDidMount() {
    for (const [index, mode] of cardModes.entries()) {
      keyboardShortcuts.registerHandler(
        "practicePage",
        `${index + 1}`.charCodeAt(0),
        () => {
          this.props.setCardMode(mode.type);
        }
      );
    }
  }

  componentWillUnmount(): void {
    for (const [index] of cardModes.entries()) {
      keyboardShortcuts.unregisterHandler(
        "practicePage",
        `${index + 1}`.charCodeAt(0)
      );
    }
  }
}

export default IntroChallengeView;
